import React, { useState, useEffect } from "react";
import { Col, Row, Form } from "react-bootstrap";
import { DatePicker } from "antd";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import toast from "react-hot-toast";

// Components
import Navbar from "../../Components/Navbar/Navbar";
import Table from "../../Components/TableTemplate/OneTable";
import DeleteModal from "../../Components/Modals/DeleteModal";
import TransactionModal from "./TransactionModal";

// API calls and utils
import {
  searchTransactions,
  deleteTransaction,
  getTransaction,
} from "../../Helpers/apiCalls/transactionApi";
import { getAllBanks } from "../../Helpers/apiCalls/banksAPi";
import { usePersistedState } from "../../Helpers/Utils/UsePersistedState";
import {
  toastStyle,
  formatDateNoTime,
  numberFormat,
  selectDropdownStyle,
  selectDropdownStyleWithPortal,
  firstDayOfMonth,
  lastDayOfMonth,
  getType,
} from "../../Helpers/Utils/Common";

// CSS
import "./Transaction.css";
import "../PettyCash/PettyCash.css";

const { RangePicker } = DatePicker;

/**
 * Transaction component
 */
function Transaction() {
  let navigate = useNavigate();
  const [inactive, setInactive] = useState(true);
  const userType = getType();

  // State for modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [modalType, setModalType] = useState("view"); // "view", "edit"

  // State for data
  const [transactionsData, setTransactionsData] = useState([]);
  const [banksData, setBanksData] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState({});
  const [showLoader, setShowLoader] = useState(true);

  // State for filters
  const [filterConfig, setFilterConfig] = usePersistedState("transaction_filters",{
    date_from: firstDayOfMonth(),
    date_to: lastDayOfMonth(),
    bank_id: "",
    bank_from_id: "",
    reference_no: "",
  });

  // Fetch transactions
  async function fetchTransactions() {
    setTransactionsData([]);
    setShowLoader(true);

    const response = await searchTransactions(filterConfig);

    if (response?.data) {
      let result = response.data.data.data.map((transaction) => {
        return {
          bank_transfer_id: transaction.id,
          transaction_date: formatDateNoTime(transaction.transaction_date),
          reference_no: transaction.reference_no,
          bank_from_name: transaction.bank_from_name,
          bank_to_name: transaction.bank_to_name,
          formatted_amount: numberFormat(transaction.amount),
          remarks: transaction.remarks || "-",
          // Store original data for modals
          original_data: {
            ...transaction,
            bank_transfer_id: transaction.id,
          },
        };
      });

      // Sort transactions by ID in descending order (newest first)
      result.sort((a, b) => b.bank_transfer_id - a.bank_transfer_id);

      setTransactionsData(result);
    }
    setShowLoader(false);
  }

  // Fetch banks for filter dropdown
  async function fetchBanks() {
    const response = await getAllBanks(filterConfig);
    if (response?.data) {
      let result = response.data.data.map((bank) => ({
        label: bank.bank_name,
        value: bank.id,
      }));
      setBanksData([{ label: "All Banks", value: "" }, ...result]);
    }
  }

  // Handle action selection (if you use action buttons)
  function handleSelectChange(e, row) {
    setSelectedTransaction({
      bank_transfer_id: row.original_data.bank_transfer_id,
      transaction_date: row.original_data.transaction_date,
      reference_no: row.original_data.reference_no,
      bank_from_id: row.original_data.bank_from_id,
      bank_to_id: row.original_data.bank_to_id,
      bank_from_name: row.original_data.bank_from_name,
      bank_to_name: row.original_data.bank_to_name,
      amount: row.original_data.amount,
      remarks: row.original_data.remarks,
    });

    if (e.target.value === "view") {
      setShowTransactionModal(true);
    } else if (e.target.value === "edit") {
      setShowTransactionModal(true);
    } else if (e.target.value === "delete") {
      setShowDeleteModal(true);
    }
  }

  // Handle row click
  async function handleRowClick(row) {
    try {
      // Fetch complete transaction details from backend
      const response = await getTransaction(row.original_data.bank_transfer_id);

      if (response?.data) {
        const transactionArray = response.data.data;
        const transactionData = transactionArray[0]; // Get first item from array

        // Map bank IDs to bank names
        const bankFromId = parseInt(transactionData.bank_from_id);
        const bankToId = parseInt(transactionData.bank_to_id);
        let bankFromData =
          banksData.find((bank) => bank.value === bankFromId) ||
          banksData.find((bank) => bank.value === transactionData.bank_from_id);
        let bankToData =
          banksData.find((bank) => bank.value === bankToId) ||
          banksData.find((bank) => bank.value === transactionData.bank_to_id);

        const bankFromName =
          bankFromData?.label || banksData[0]?.label || "Select Bank";
        const bankToName =
          bankToData?.label || banksData[0]?.label || "Select Bank";

        setSelectedTransaction({
          bank_transfer_id: transactionData.id,
          transaction_date: transactionData.transaction_date,
          reference_no: transactionData.reference_no,
          bank_from_id: transactionData.bank_from_id,
          bank_to_id: transactionData.bank_to_id,
          bank_from_name: bankFromName,
          bank_to_name: bankToName,
          amount: transactionData.amount,
          remarks: transactionData.remarks,
        });
        setShowTransactionModal(true);
      } else {
        toast.error("Error loading transaction details", {
          style: toastStyle(),
        });
      }
    } catch (error) {
      toast.error("Error loading transaction details", {
        style: toastStyle(),
      });
    }
  }

  // Delete transaction
  async function handleDelete() {
    const response = await deleteTransaction(
      selectedTransaction.bank_transfer_id
    );
    if (response?.data) {
      toast.success("Transaction deleted successfully!", {
        style: toastStyle(),
      });
      setShowDeleteModal(false);
      fetchTransactions();
    } else {
      toast.error("Error deleting transaction", {
        style: toastStyle(),
      });
    }
  }

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilterConfig((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Initialize data
  useEffect(() => {
    fetchTransactions();
  }, [filterConfig]);

  useEffect(() => {
    fetchBanks();
  }, [filterConfig]);

  return (
    <div>
      <div className="page">
        <Navbar
          onCollapse={(inactive) => {
            setInactive(inactive);
          }}
          active={"TRANSFER"}
        />
      </div>
      <div className={`manager-container ${inactive ? "inactive" : "active"}`}>
        {/* Header */}
        <Row className="mb-4">
          <Col xs={6}>
            <h1 className="page-title">BANK TO BANK TRANSFERS</h1>
          </Col>
          <Col className="d-flex justify-content-end">
            {(userType === "admin" ||
              userType === "finance_accounting_officer") && (
              <button
                className="add-btn"
                onClick={() => navigate("/transaction/add")}
              >
                Add
              </button>
            )}
          </Col>
        </Row>

        {/* Content with integrated filters */}
        <div className="tab-content">
          {/* Filters */}
          <div className="my-2 ms-2 PO-filters PI-filters d-flex">
            <RangePicker
              format="YYYY-MM-DD"
              placeholder={[filterConfig.date_from, filterConfig.date_to]}
              onChange={(dates) => {
                if (dates) {
                  handleFilterChange(
                    "date_from",
                    dates[0].format("YYYY-MM-DD")
                  );
                  handleFilterChange("date_to", dates[1].format("YYYY-MM-DD"));
                } else {
                  handleFilterChange("date_from", firstDayOfMonth());
                  handleFilterChange("date_to", lastDayOfMonth());
                }
              }}
              className="me-2"
            />
            <Select
              className="dropsearch-filter px-0 py-0 me-2"
              classNamePrefix="react-select"
              placeholder="Select Bank"
              value={banksData.find((b) => b.value === filterConfig.bank_from_id) ?? null}
              options={banksData}
              styles={selectDropdownStyleWithPortal}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              onChange={(e) => handleFilterChange("bank_from_id", e?.value || "")}
            />
          </div>

          {/* Table */}
          <Table
            tableHeaders={[
              "DATE",
              "REFERENCE",
              "FROM",
              "TO",
              "AMOUNT",
              "REMARKS",
            ]}
            headerSelector={[
              "transaction_date",
              "reference_no",
              "bank_from_name",
              "bank_to_name",
              "formatted_amount",
              "remarks",
            ]}
            tableData={transactionsData}
            showLoader={showLoader}
            withActionData={false}
            rowClickHandler={handleRowClick}
          />
        </div>
      </div>

      {/* Modals */}
      <DeleteModal
        text="transaction"
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
      />

      <TransactionModal
        show={showTransactionModal}
        onHide={() => setShowTransactionModal(false)}
        transaction={selectedTransaction}
        onUpdate={() => {
          setShowTransactionModal(false);
          fetchTransactions();
        }}
      />
    </div>
  );
}

export default Transaction;
